import React from 'react';
import { Requirement } from '../../auth/permissions';
import { PRINTFULL_ID } from '../../constants';
import StoriesPage from 'screens/Stories';
import StoryTagsPage from 'screens/StoryTags';
import StoriesTagsPage from 'screens/StoriesTags';
import PrintfulPage from 'screens/Printful';
import OrdersAnalytics from 'screens/OrdersAnalytics';
import StoryPage from 'screens/Stories/OneStory';
import OrderPage from 'screens/Printful/OneOrder';
import CreateStoryPage from 'screens/Stories/CreateStory';
import Authors from 'screens/Authors';
import Analytics3D from 'screens/3dAnalytics';
import TrackOrder from 'screens/TrackOrder';
import ComingSoon from 'screens/ComingSoon';
import MockupHistory from 'screens/MockupHistory';
import Mockups from 'screens/Mockups';
import MockupsColoring from 'screens/MockupsColoring';
import MockupsTowel from 'screens/MockupsTowel';
import MockupsStats from 'screens/MockupsStats';
import MockupsCustomers from 'screens/MockupsCustomers';
import MockupsCompareStats from 'screens/MockupsCompareStats';
import RemovebgLogs from 'screens/RemovebgLogs/RemovebgLogs';
import HealthCheckPage from 'screens/HealthCheck';
import SessionConversionPage from 'screens/SessionConversion';
import MockupLogsDashboardPage from 'screens/MockupLogsDashboard';
import StoriesStats from 'screens/Stories/Stats';
import AiTestPage from 'screens/AiTest';
import ShipmentIssuesPage from 'screens/ShipmentIssues';
import OrdersTrackingPage from 'screens/OrdersTracking';
import PillowSegmentationPage from 'screens/PillowSegmentation';
import ModelFrontLogsPage from 'screens/ModelFrontLogs';
import OneModelFrontLogPage from 'screens/ModelFrontLogs/OneModelFrontLog';
import AavOrdersPage from 'screens/AavOrders';
import OneAavOrderPage from 'screens/AavOrders/OneAavOrder';
import CustomerFormSubmissionsPage from 'screens/CustomerFormSubmissions';
import OneCustomerFormSubmissionPage from 'screens/CustomerFormSubmissions/OneCustomerFormSubmission';
import MockupGenerationLogsPage from 'screens/MockupGenerationLogs';
import OneMockupGenerationLogPage from 'screens/MockupGenerationLogs/OneMockupGenerationLog';
import PrintFileJobsPage from 'screens/PrintFileJobs';
import PrintFileReviewPage from 'screens/PrintFileReview';
import StoryViewerPage from 'screens/StoryViewer';
import {
    MODERATOR, ORDERS_R, STORIES_R, STORIES_RW, STORY_VIEWER, MOCKUP_HISTORY_R, MOCKUP_R, AAV_PRODUCTION_R, AAV_SHOPIFY_R,
} from '../../auth/requirements';

export interface AppRoute {
    path: string;
    element: JSX.Element;
    requires: Requirement;
}

/**
 * Every page of the admin panel with the right it needs. `routes()` keeps the entries the
 * signed-in user satisfies; moderators get all of them.
 */
export const APP_ROUTES: AppRoute[] = [
    // Order integration (order-api)
    { path: '/:id/orders', element: <PrintfulPage />, requires: ORDERS_R },
    { path: ':companyId/orders/:id/details', element: <OrderPage />, requires: ORDERS_R },
    { path: '/orders-analytics', element: <OrdersAnalytics />, requires: ORDERS_R },
    { path: '/shipment-issues', element: <ShipmentIssuesPage />, requires: ORDERS_R },
    { path: '/orders-tracking', element: <OrdersTrackingPage />, requires: ORDERS_R },
    { path: '/comingSoon', element: <ComingSoon />, requires: MODERATOR },
    { path: '/companies', element: <ComingSoon />, requires: MODERATOR },
    { path: '/products', element: <ComingSoon />, requires: MODERATOR },

    // Customer stories (story admin API)
    { path: '/stories', element: <StoriesPage />, requires: STORIES_R },
    { path: '/stories/stats', element: <StoriesStats />, requires: STORIES_R },
    { path: '/stories/create', element: <CreateStoryPage />, requires: STORIES_RW },
    { path: '/stories/:id/details', element: <StoryPage />, requires: STORIES_R },
    { path: '/s-tags', element: <StoriesTagsPage />, requires: STORIES_R },
    { path: '/story-tags', element: <StoryTagsPage />, requires: STORIES_R },
    { path: '/authors', element: <Authors />, requires: STORIES_R },
    { path: '/story-viewer', element: <StoryViewerPage />, requires: STORY_VIEWER },

    // Mockups (mockup admin API)
    { path: '/mockups-image', element: <Mockups />, requires: MOCKUP_HISTORY_R },
    { path: '/mockups-stats', element: <MockupsStats />, requires: MOCKUP_HISTORY_R },
    { path: '/mockups-compare-stats', element: <MockupsCompareStats />, requires: MOCKUP_R },
    { path: '/3d-mockups', element: <MockupHistory />, requires: MOCKUP_HISTORY_R },
    { path: '/mockups-coloring', element: <MockupsColoring />, requires: MOCKUP_R },
    { path: '/mockups-towel', element: <MockupsTowel />, requires: MOCKUP_R },
    { path: '/mockups-customers', element: <MockupsCustomers />, requires: MOCKUP_R },
    { path: '/removebg-logs', element: <RemovebgLogs />, requires: MOCKUP_R },
    { path: '/mockup-logs', element: <MockupLogsDashboardPage />, requires: MOCKUP_R },
    { path: '/mockup-generation-logs', element: <MockupGenerationLogsPage />, requires: MOCKUP_R },
    { path: '/mockup-generation-logs/:eventId', element: <OneMockupGenerationLogPage />, requires: MOCKUP_R },
    { path: '/health-check', element: <HealthCheckPage />, requires: MOCKUP_R },
    { path: '/session-conversion', element: <SessionConversionPage />, requires: MOCKUP_R },
    { path: '/3d-analytics', element: <Analytics3D />, requires: MOCKUP_R },
    { path: '/ai-test', element: <AiTestPage />, requires: MODERATOR },

    // AAV production (print files, pillow search)
    { path: '/print-file-jobs', element: <PrintFileJobsPage />, requires: AAV_PRODUCTION_R },
    { path: '/print-file-review', element: <PrintFileReviewPage />, requires: AAV_PRODUCTION_R },
    { path: '/track-order', element: <TrackOrder />, requires: AAV_PRODUCTION_R },
    { path: '/pillow-segmentation', element: <PillowSegmentationPage />, requires: AAV_PRODUCTION_R },

    // AAV Shopify (AAV orders, form submissions; includes everything in AAV production)
    { path: '/model-front-logs', element: <ModelFrontLogsPage />, requires: MODERATOR },
    { path: '/model-front-logs/:logId', element: <OneModelFrontLogPage />, requires: MODERATOR },
    { path: '/aav-orders', element: <AavOrdersPage />, requires: AAV_SHOPIFY_R },
    { path: '/aav-orders/:orderId', element: <OneAavOrderPage />, requires: AAV_SHOPIFY_R },
    { path: '/customer-form-submissions', element: <CustomerFormSubmissionsPage />, requires: AAV_SHOPIFY_R },
    { path: '/customer-form-submissions/:submissionId', element: <OneCustomerFormSubmissionPage />, requires: AAV_SHOPIFY_R },
];

/** Where `/` goes: the first page in this list the user may open. */
export const LANDING_CANDIDATES: { path: string; requires: Requirement }[] = [
    { path: `/${PRINTFULL_ID}/orders`, requires: ORDERS_R },
    { path: '/print-file-review', requires: AAV_PRODUCTION_R },
    { path: '/stories', requires: STORIES_R },
    { path: '/story-viewer', requires: STORY_VIEWER },
    { path: '/mockups-image', requires: MOCKUP_HISTORY_R },
    { path: '/customer-form-submissions', requires: AAV_SHOPIFY_R },
];
